import { useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { productStatus, subscriptionPeriod } from "../../../enums";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  ICreateProduct,
  ICreateEditProductFeature,
  IHookForm,
  IModalFormWithTableProps,
  IPageAndSearchRequest,
  IViewBankAccount,
  PagedMeta,
  IViewProduct,
  IFormWithTableProps,
} from "../../../interfaces-and-types";
import { convertObjectToFormData, globalHandlerUtils } from "../../../utils";

import { View } from "./View";

export interface ICreateProductFormViewProps
  extends IHookForm<ICreateProduct>,
    IModalFormWithTableProps {
  isLoadingBankAccounts: boolean;
  activePayoutTags: IViewBankAccount[];
  formValues: ICreateProduct;
  saveProductIcon: (e: React.ChangeEvent<HTMLInputElement>) => void;
  actualImage?: string;
}

export const CreateProductForm = (
  props: IFormWithTableProps & {
    goBackOnClose: boolean; // NOTE: used for on-the-fly product creation eg while creating a payment link
  }
) => {
  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();
  const location = useLocation();

  const closeModal = () => {
    if (props.goBackOnClose) navigate(-1);
    else
      navigateToRoutePreserveParams(location.pathname.replace("/create", ""));
  };

  const {
    isLoading: isLoadingBankAccounts,
    isSuccess: isSuccessBankAccounts,
    data: withdrawalBankAccounts,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IViewBankAccount[],
    PagedMeta
  >([
    "getBankAccounts",
    {
      page: 1,
      page_size: 5000,
      search: null,
    },
  ]);

  const { isSuccess: isSuccessSaveProduct, mutate: createProduct } =
    useMerchantApiMutation</*ICreateProduct*/ FormData, IViewProduct>(
      "createProduct",
      [],
      {
        headers: { "content-type": "multipart/form-data" },
      }
    ).usePostMutation;

  useEffect(() => {
    if (isSuccessSaveProduct) {
      props.refreshTable();
      closeModal();
      reset();
    }
  }, [isSuccessSaveProduct]);

  const initialFormValues: ICreateProduct = {
    product_icon: null,
    product_name: "",
    product_description: "",
    payout_tag: null,
    price: 0,
    currency: "",
    is_subscription_product: false,
    subscription_period: subscriptionPeriod.daily,
    status: productStatus.active,

    // qty tracking
    enable_quantity_tracking: false,
    enable_product_feature_based_pricing: false,

    product_features: [],
    product_feature_value_quantities: [],
  };

  const createProductSchema: yup.SchemaOf<ICreateProduct> = yup
    .object({
      product_icon: yup.mixed().nullable().optional(),
      product_name: yup.string().required("Product Name is required"),
      product_description: yup.string().nullable().optional(),
      payout_tag: yup.string().nullable().optional(),
      price: yup
        .number()
        .transform((_value, originalValue) =>
          !originalValue
            ? 0
            : Number(originalValue.toString().replace(/,/g, ""))
        )
        .required("price is required")
        .positive("Amount must be greater than zero"),
      currency: yup.string().required("Currency is required"),
      is_subscription_product: yup
        .boolean()
        .required("Subscription Type is required"),
      subscription_period: yup
        .mixed<subscriptionPeriod>()
        .oneOf(Object.values(subscriptionPeriod))
        .required("Subscription Period is required"),
      status: yup
        .mixed<productStatus>()
        .oneOf(Object.values(productStatus))
        .required("status is required"),

      // qty tracking
      enable_quantity_tracking: yup
        .boolean()
        .required("Quantity tracking status is required"),
      enable_product_feature_based_pricing: yup
        .boolean()
        .required("Feature based pricing status is required"),

      product_features: yup.array().of(
        yup.object().shape({
          id: yup.string().required(),
          name: yup.string().required("Feature name is required"),
        })
      ),
      product_feature_value_quantities: yup.array().of(
        yup.object().shape({
          id: yup.string().required(),
          quantity_available: yup
            .number()
            .required("Quantity available is required"),

          product_feature_values: yup.array().of(
            yup.object().shape({
              id: yup.string().required(),
              name: yup.string().required("Feature name is required"),
              value: yup.string().required("Feature value is required"),
            })
          ),
        })
      ),
    })
    .required();

  const [productIconFile, setProductIconFile] = useState<File | null>(null);
  const [actualImage, setActualImage] = useState<string | undefined>(undefined);

  const saveProductIcon = (e: React.ChangeEvent<HTMLInputElement>) => {
    let reader = new FileReader();
    reader.onload = function (event: any) {
      // (document.querySelector(".image-previewer")! as HTMLImageElement).src =
      //   event.target.result;
      setActualImage(event.target.result);
    };
    if (e.target.files && e.target.files[0]) {
      setProductIconFile(e.target.files[0]);
      reader.readAsDataURL(e.target.files[0]);
      document
        .querySelector(".image-previewer")!
        .classList.add("remove-border");
    }
  };

  const handleApiSubmit = (data: ICreateProduct) => {
    const stateAsFormData = convertObjectToFormData(initialFormValues, data, [
      { uploadFileName: "product_icon", file: productIconFile },
    ]);

    createProduct(stateAsFormData);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, control, reset, watch } = useHookForm(
    {
      initialFormValues,
      validationSchema: createProductSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    }
  );

  const {
    fields: product_featuresFields,
    append: appendProductFeaturesField,
    remove: removeProductFeaturesField,
  } = useFieldArray({ name: "product_features", control });

  const {
    fields: product_feature_value_quantities,
    append: appendProductFeatureValueQuantitiesField,
    remove: removeProductFeatureValueQuantitiesField,
  } = useFieldArray({ name: "product_feature_value_quantities", control });

  // const appendProductFeaturesFieldWithQuantities = (name: string) =>{
  //   appendProductFeaturesField({
  //     name
  //   });

  //   appendProductFeatureValueQuantitiesField({
  //     quantity_available: 0,
  //     product_feature_values:
  //   })
  // }

  // const removeProductFeaturesFieldWithQuantities = (index: number) =>
  //   appendProductFeaturesField({
  //     index
  //   });

  const formValues = watch();

  const activePayoutTagBankAccounts =
    withdrawalBankAccounts?.filter(
      (acc) => !acc.is_disabled && acc.bank_account_tags.length
    ) || [];

  return (
    <View
      {...props}
      isModalOpen
      closeModal={closeModal}
      isLoadingBankAccounts={isLoadingBankAccounts}
      activePayoutTags={activePayoutTagBankAccounts}
      handleSubmit={onFormSubmit}
      saveProductIcon={saveProductIcon}
      register={register}
      errors={errors}
      formValues={formValues}
      actualImage={actualImage}
    />
  );
};
